<template>
  <div
    class="fixed inset-0 z-[9999] bg-[#0A1A38] flex items-center justify-center overflow-hidden transition-opacity duration-1000 antialiased"
  >
    <!-- 1. DYNAMIC BACKGROUND ELEMENTS -->
    <div class="absolute w-[800px] h-[800px] bg-[#2AD1EE] rounded-full blur-[200px] opacity-10 animate-pulse-slow"></div>
    <div class="absolute inset-0 opacity-5" style="background-image: radial-gradient(#2AD1EE 0.5px, transparent 0.5px); background-size: 40px 40px;"></div>

    <!-- 2. SLOT MACHINE LOADER -->
    <div class="relative flex flex-col items-center">
      <!-- Slot Machine Frame -->
      <div class="relative p-6 bg-gradient-to-b from-[#1E2E4D] to-[#0A1A38] rounded-2xl border-4 border-[#2AD1EE]/30 shadow-[0_0_50px_rgba(42,209,238,0.2)]">
        
        <!-- Cabinet Top/Header -->
        <div class="mb-4 text-center">
          <div class="text-[14px] text-white uppercase tracking-[0.4em] font-black font-bro-mega">
            Zach <span class="text-[#2AD1EE]">Dev Works</span>
          </div>
        </div>

        <!-- The Reels Container -->
        <div class="flex gap-3 bg-black/40 p-4 rounded-xl border border-white/5 overflow-hidden">
          <div v-for="(reel, i) in reels" :key="i" class="relative w-20 h-32 overflow-hidden bg-black/60 rounded-lg border border-[#2AD1EE]/10">
            <!-- Reel Strip -->
            <div 
              class="flex flex-col items-center gap-6"
              :style="{ 
                transform: `translateY(-${reelsOffset[i]}px)`,
                transition: progress >= 100 ? 'transform 1s cubic-bezier(0.12, 0, 0.39, 0)' : 'none'
              }"
            >
              <!-- Duplicate symbols twice for infinite loop effect -->
              <div v-for="(symbol, sIdx) in extendedSymbols" :key="sIdx" class="w-16 h-16 flex items-center justify-center text-3xl shrink-0">
                <img v-if="symbol === 'LOGO'" :src="Logo" class="w-10 h-10 object-contain" />
                <span v-else>{{ symbol }}</span>
              </div>
            </div>
            
            <!-- Glass Overlay/Shadow -->
            <div class="absolute inset-0 pointer-events-none shadow-[inset_0_15px_25px_rgba(0,0,0,0.9),inset_0_-15px_25px_rgba(0,0,0,0.9)] z-20"></div>
            <div class="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-[#2AD1EE]/20 blur-sm z-30"></div>
          </div>
        </div>

        <!-- Jackpot Indicator Lights -->
        <div class="mt-4 flex justify-center items-center gap-2">
          <div v-for="n in 5" :key="n" 
            class="w-2 h-2 rounded-full transition-colors duration-300"
            :class="[progress >= (n * 20) ? 'bg-[#2AD1EE] shadow-[0_0_8px_#2AD1EE]' : 'bg-white/5', (n === 1 || n === 5) ? 'animate-pulse' : '']"
          ></div>
        </div>
      </div>

      <!-- Jackpot Counter -->
      <div class="mt-10 text-center">
        <div class="text-white/30 text-[10px] uppercase tracking-[0.5em] mb-2 font-lato">Current Multiplayer</div>
        <div class="text-6xl font-black font-bro-mega text-white tracking-widest leading-none">
          {{ Math.round(progress) }}<span class="text-[#2AD1EE] text-2xl">%</span>
        </div>
        
        <!-- Status Text -->
        <div class="mt-8 relative h-4">
          <transition name="fade-slide" mode="out-in">
            <p :key="currentStatus" class="text-[#2AD1EE] text-[10px] uppercase tracking-[0.4em] font-lato">
              {{ currentStatus }}
            </p>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import Logo from "@/assets/zach_dev_works_logo.avif";

const symbols = ["🍒", "🍋", "7️⃣", "💎", "🔔", "🍊", "🌟", "LOGO"];
// Extend symbols to ensure we always have enough for a seamless wrap
const extendedSymbols = [...symbols, ...symbols, ...symbols, ...symbols];

const reels = [0, 1, 2];
const progress = ref(0);
const reelsOffset = ref([0, 0, 0]);
const currentStatus = ref("Initializing");
let rafId;

// Constants for positioning
// Symbol height = 64px, Gap = 24px (gap-6) => 88px total per symbol
const STEP = 88;
const SYMBOL_COUNT = symbols.length;
const TOTAL_HEIGHT = SYMBOL_COUNT * STEP;

onMounted(() => {
  const DURATION = 2500;
  const start = performance.now();
  
  const tick = (now) => {
    const elapsed = now - start;
    const t = Math.min(1, elapsed / DURATION);
    
    progress.value = t * 100;

    if (t < 0.25) currentStatus.value = "Placing Bets...";
    else if (t < 0.6) currentStatus.value = "Spinning Reels...";
    else if (t < 0.85) currentStatus.value = "Checking Odds...";
    else currentStatus.value = "JACKPOT!";

    // High performance spin logic
    reelsOffset.value = reelsOffset.value.map((offset, i) => {
      if (t < 0.99) {
        // Continuous spinning based on time
        const speed = (30 + i * 10);
        return (offset + speed) % TOTAL_HEIGHT;
      } else {
        // Smoothly snap to Logo
        // Offset 584 centers the 8th symbol (index 7) in a 128px container
        return 584;
      }
    });

    if (elapsed < DURATION) {
      rafId = requestAnimationFrame(tick);
    } else {
      progress.value = 100;
      reelsOffset.value = [584, 584, 584];
    }
  };
  
  rafId = requestAnimationFrame(tick);
});

onBeforeUnmount(() => {
  if (rafId) cancelAnimationFrame(rafId);
});
</script>

<style scoped>
.font-bro-mega {
  font-family: 'Bro Mega', sans-serif;
}

@keyframes pulse-slow {
  0%, 100% { opacity: 0.05; transform: scale(1); }
  50% { opacity: 0.15; transform: scale(1.1); }
}

.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.2s ease;
}
.fade-slide-enter-from { opacity: 0; transform: translateY(5px); }
.fade-slide-leave-to { opacity: 0; transform: translateY(-5px); }
</style>
